import { useFormContext, useWatch } from 'react-hook-form';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useFormDefaultValues } from '../../FormDefaultValuesProvider';

// Helper to display values nicely
function formatValue(value) {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

// Helper to check if a field is dirty
function isDirty(fieldName, dirtyFields) {
  if (!dirtyFields) return false;

  // For nested objects, dirtyFields may be an object
  if (typeof dirtyFields === 'object') {
    const keys = fieldName.split('.');
    const result = keys.reduce(
      (acc, key) => (acc && acc[key] ? acc[key] : undefined),
      dirtyFields,
    );
    return !!result;
  }

  return !!dirtyFields[fieldName];
}

function LiveFormValuesPreviewTable() {
  const { control, formState } = useFormContext();
  const { formDefaultValues } = useFormDefaultValues();
  const values = useWatch({ control }) || {};
  const { dirtyFields } = formState;

  const entries = Object.entries(values).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Default Value</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Current Value</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Dirty</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {entries.map(([key, value]) => (
            <TableRow key={key} hover>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  verticalAlign: 'top',
                }}>
                {key}
              </TableCell>

              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                {formatValue(formDefaultValues[key])}
              </TableCell>

              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                {formatValue(value)}
              </TableCell>

              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 13,
                  textAlign: 'center',
                }}>
                {isDirty(key, dirtyFields) ? '✅' : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default LiveFormValuesPreviewTable;
